import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import AdminLayout, { adminFetch, KpiCard, SectionTitle, AdminCard, Badge, useAdmin } from './AdminLayout';

/**
 * AdminDashboard — page admin (Phase 6 / G2 — migration endpoint TS).
 *
 * Backend : `GET /api/admin/analytics/summary` retourne `AdminDashboardOutput`
 * (cf. backend/src/contexts/analytics/application/dto/AdminDashboardOutput.ts) :
 *   { totalLeads, totalDossiersActifs,
 *     kpis: { range, computedAt, from, to, dossiersByStatus, matchingsByDay,
 *             topAgents, conversion: { matchingCount, dossierCount, rate } },
 *     recentActivity, computedAt }
 *
 * L'ancien shape comportait kpi.profilages_today/week/month, last_payments,
 * traffic, server_status, aides_count, last_cron — ces données ne sont
 * plus exposées par la TS (pivot interne : on n'a plus de paywall ni
 * de cron public). On affiche ce que le nouveau endpoint donne.
 */
export default function AdminDashboard() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoadError(false);
      const res = await adminFetch('/api/admin/analytics/summary');
      if (res.ok) {
        setData(await res.json());
      } else {
        setLoadError(true);
      }
    } catch {
      setLoadError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const fmtDate = ts => ts ? new Date(ts).toLocaleString('fr-FR') : '—';

  return (
    <AdminLayout title="Tableau de bord">
      <DashboardContent data={data} loading={loading} loadError={loadError} fmtDate={fmtDate} onRefresh={load} />
    </AdminLayout>
  );
}

function DashboardContent({ data, loading, loadError, fmtDate, onRefresh }) {
  const { dark, cardBg, border } = useAdmin();
  const textColor = dark ? '#e0e0e0' : '#2D2D2D';
  const mutedColor = '#8A8A8A';

  if (loading) return <LoadingState />;
  if (loadError || !data) return <ErrorState onRetry={onRefresh} />;

  const { totalLeads = 0, totalDossiersActifs = 0, kpis, recentActivity = [], computedAt } = data;
  const conversion = kpis?.conversion || { matchingCount: 0, dossierCount: 0, rate: 0 };
  const matchingsByDay = Array.isArray(kpis?.matchingsByDay) ? kpis.matchingsByDay : [];
  const dossiersByStatus = Array.isArray(kpis?.dossiersByStatus) ? kpis.dossiersByStatus : [];
  const topAgents = Array.isArray(kpis?.topAgents) ? kpis.topAgents : [];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: 13, color: mutedColor }}>
          Snapshot calculé le {fmtDate(computedAt)} · Fenêtre KPI : 30 derniers jours · Actualisation auto toutes les 30s
        </div>
        <button onClick={onRefresh} style={{
          padding: '8px 16px', borderRadius: 8, border: '1px solid #D9D9D9',
          background: 'transparent', color: '#D4724A', fontSize: 13, cursor: 'pointer',
        }}>↻ Actualiser</button>
      </div>

      {/* Big numbers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 32 }}>
        <KpiCard label="Total leads" value={totalLeads} icon="📋" color="#D4724A" />
        <KpiCard label="Dossiers actifs" value={totalDossiersActifs} icon="📂" color="#2980B9" />
        <KpiCard label="Matchings (30j)" value={conversion.matchingCount} sub="Calculs d'éligibilité" icon="🎯" color="#F0A500" />
        <KpiCard label="Dossiers créés (30j)" value={conversion.dossierCount} sub="Suite à un matching" icon="✅" color="#3D8B5E" />
        <KpiCard
          label="Taux de conversion"
          value={`${(conversion.rate * 100).toFixed(1)}%`}
          sub="Matchings → dossiers"
          icon="📈"
          color="#5C8C6E"
        />
      </div>

      {/* Graph matchings/jour */}
      <AdminCard style={{ marginBottom: 24 }}>
        <SectionTitle>Matchings — 30 derniers jours</SectionTitle>
        {matchingsByDay.length === 0 ? (
          <p style={{ fontSize: 13, color: mutedColor, marginTop: 8 }}>
            Aucun matching enregistré sur la période.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={matchingsByDay} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={dark ? '#2d2d4e' : '#F0EDE8'} />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 11, fill: mutedColor }}
                tickFormatter={d => (typeof d === 'string' ? d.slice(5) : '')}
              />
              <YAxis tick={{ fontSize: 11, fill: mutedColor }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 8, fontSize: 13 }}
                labelFormatter={d => `Date : ${d}`}
                formatter={v => [v, 'Matchings']}
              />
              <Line
                type="monotone" dataKey="count" stroke="#D4724A"
                strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#D4724A' }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </AdminCard>

      {/* Bottom row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
        {/* Dossiers par statut */}
        <AdminCard>
          <SectionTitle>Dossiers par statut</SectionTitle>
          {dossiersByStatus.length === 0 ? (
            <p style={{ fontSize: 13, color: mutedColor }}>Aucun dossier en base.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {dossiersByStatus.map((s) => (
                <div key={s.status} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: dark ? '#1a1a2e' : '#F5F3F0', borderRadius: 8 }}>
                  <span style={{ fontSize: 14, color: textColor, fontWeight: 500 }}>{s.status}</span>
                  <Badge color="#5C8C6E">{s.count}</Badge>
                </div>
              ))}
            </div>
          )}
        </AdminCard>

        {/* Top agents */}
        <AdminCard>
          <SectionTitle>Agents les plus actifs (30j)</SectionTitle>
          {topAgents.length === 0 ? (
            <p style={{ fontSize: 13, color: mutedColor }}>Aucune activité agent enregistrée.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {topAgents.map((a, i) => (
                <div key={a.agentId || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: dark ? '#1a1a2e' : '#F5F3F0', borderRadius: 8 }}>
                  <span style={{ fontSize: 13, color: textColor, fontFamily: 'monospace' }}>
                    {(i + 1) + '. '}{a.agentId || 'inconnu'}
                  </span>
                  <Badge color="#D4724A">{a.dossierCount ?? a.count ?? 0}</Badge>
                </div>
              ))}
            </div>
          )}
        </AdminCard>
      </div>

      {/* Activité récente (audit) */}
      <AdminCard>
        <SectionTitle>Activité récente (audit)</SectionTitle>
        {recentActivity.length === 0 ? (
          <p style={{ fontSize: 13, color: mutedColor }}>Aucune activité récente.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${border}` }}>
                  {['Date', 'Agent', 'Action', 'Ressource'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: mutedColor, fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentActivity.map((entry, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${border}` }}>
                    <td style={{ padding: '8px 12px', color: textColor, whiteSpace: 'nowrap' }}>
                      {fmtDate(entry.timestamp || entry.createdAt)}
                    </td>
                    <td style={{ padding: '8px 12px', color: textColor, fontFamily: 'monospace', fontSize: 12 }}>
                      {entry.agentId || '—'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge color="#2980B9">{entry.action || '—'}</Badge>
                    </td>
                    <td style={{ padding: '8px 12px', color: textColor }}>
                      {entry.resourceType || '—'}
                      {entry.resourceId && (
                        <span style={{ color: mutedColor, marginLeft: 6, fontFamily: 'monospace', fontSize: 11 }}>
                          {entry.resourceId.slice(0, 8)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>
    </div>
  );
}

function LoadingState() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
      {[...Array(8)].map((_, i) => (
        <div key={i} style={{
          height: 100, background: '#F5F3F0', borderRadius: 16,
          animation: 'pulse 1.5s infinite', opacity: 0.6,
        }} />
      ))}
    </div>
  );
}

function ErrorState({ onRetry }) {
  return (
    <div style={{ textAlign: 'center', padding: 64, color: '#8A8A8A' }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
      <p>Impossible de charger les données. Vérifiez que le backend est démarré.</p>
      {onRetry && (
        <button onClick={onRetry} style={{
          marginTop: 16, padding: '10px 22px', borderRadius: 8, border: 'none',
          background: '#D4724A', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}>Réessayer</button>
      )}
    </div>
  );
}
